import { PilotCard } from "features/pilot-tracking/pilots/PilotCard"
import { UserProfileModalTrigger } from "features/users/UserProfileModal"
import { ExpandableContent } from "@nova/component-expandable-content"
import { ButtonPrimary } from "@nova/component-btn"
import { FunctionComponent, ReactNode, useCallback, useMemo, useState } from "react"
import { MemberExtended } from "@nova/schemas"
import * as Icons from "@nova/component-icons"
import { usePilots } from "features/pilot-tracking/pilots/PilotQueries"
import { Table } from "@nova/ds-component-table"
import { createQuery } from "@nova/queryable"
import { usePilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import { Status } from "@nova/ds-component-status"
import { InputOutlined } from "@nova/ds-component-input"
import { useRouter } from "pages"
import { useParams } from "react-router"
import { selectSearch, selectView, setSearch, setView } from "features/pilot-tracking/pilots/slice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { getGenericWorkspacePage, getGenericWorkspacePageHeader } from "../WorkspaceRoutes"
import { useLoadingText } from "features/data-management/workspace/workspace/useLoadingText"
import { ViewSwitcher } from "features/data-management/workspace/workspace/body/navigation-bar/breadcrumb-bar/view-switcher/ViewSwitcher"
import { LinkText } from "components/LinkText"
import { manuallyTruncateLongString, pluralize } from "@nova/util-strings"
import { Tooltip } from "panels/content/top-bar/TODO-put-in-package/Tooltip"
import {
    summarizeCurrencyStatuses,
    usePilotCurrencyStatuses,
} from "features/pilot-tracking/currency/usePilotCurrencyStatuses"

export const PilotsPage = getGenericWorkspacePage({
    Header: getGenericWorkspacePageHeader({
        title: "All Pilots",
        rightItems: [
            () => {
                const dispatch = useAppDispatch()
                const view = useAppSelector(selectView)
                return (
                    <ViewSwitcher
                        firstOption={{
                            icon: Icons.Grid,
                            isSelected: view === "grid",
                            onSwitch: () => dispatch(setView("grid")),
                        }}
                        secondOption={{
                            icon: Icons.List,
                            isSelected: view === "table",
                            onSwitch: () => dispatch(setView("table")),
                        }}
                    />
                )
            },
            () => {
                const dispatch = useAppDispatch()
                const search = useAppSelector(selectSearch)
                return (
                    <div className="min-w-[228px] px-px">
                        <InputOutlined
                            placeholder="Search"
                            leftItem={<Icons.Icon Icon={Icons.MagnifyingGlass} />}
                            value={search}
                            onChange={(val) => dispatch(setSearch(val.currentTarget.value))}
                            size="sm"
                        />
                    </div>
                )
            },
        ],
    }),
    Content: PilotsPageContent,
    maxWidth: "unbound",
})

function PilotsPageContent() {
    const search = useAppSelector(selectSearch)
    const view = useAppSelector(selectView)
    const { pilots, activeUserIds, isLoading } = usePilots({ search })
    const sortedPilots = useMemo(() => {
        return pilots?.toSorted((a, b) => (a.fullName || "").localeCompare(b.fullName || ""))
    }, [pilots])
    if (isLoading) {
        return (
            <div className="w-full h-full flex justify-center items-center">
                <div className="loader" />
            </div>
        )
    }
    if (!sortedPilots?.length) {
        return (
            <div className="flex flex-col items-center gap-2">
                <h1 className="text-white">No Pilots Found</h1>
                <h5 className="text-gray-100">
                    Pilots will appear when they have been assigned to a flight.
                </h5>
            </div>
        )
    }
    return (
        <div className="pb-2 h-full flex flex-col gap-4">
            {view === "grid" ? (
                <PilotGridLayout pilots={sortedPilots} activeUserIds={activeUserIds} />
            ) : (
                <PilotTableView pilots={sortedPilots} activeUserIds={activeUserIds} />
            )}
        </div>
    )
}

const PilotGridLayout: FunctionComponent<PilotViewProps> = ({ pilots, activeUserIds }) => {
    const router = useRouter("/")
    const { workspaceId } = useParams() as { workspaceId: string }
    const { statusesByUser } = usePilotCurrencyStatuses()
    const activePilots = pilots.filter((p) => activeUserIds.has(p.id))
    const inactivePilots = pilots.filter((p) => !activeUserIds.has(p.id))
    return (
        <div className="w-full flex justify-center">
            <div className="max-w-[1200px] w-full flex flex-col gap-4 pt-1.5">
                <PilotCollapsibleSection label="Active" defaultOpen pilots={activePilots}>
                    <PilotCardGrid>
                        {activePilots.map((pilot) => {
                            if (!pilot.id) return null
                            return (
                                <router.Link
                                    key={pilot.id}
                                    to={{
                                        path: "/workspace/:workspaceId/fleet/pilots/:pilotId",
                                        params: { workspaceId, pilotId: pilot.id },
                                    }}
                                    className="h-fit w-full"
                                >
                                    <PilotCard
                                        user={pilot}
                                        currencyStatuses={statusesByUser.get(pilot.id)}
                                    />
                                </router.Link>
                            )
                        })}
                    </PilotCardGrid>
                </PilotCollapsibleSection>
                <PilotCollapsibleSection label="Inactive" pilots={inactivePilots}>
                    <PilotCardGrid>
                        {inactivePilots.map((pilot) => {
                            if (!pilot.id) return null
                            return (
                                <UserProfileModalTrigger
                                    key={pilot.id}
                                    userId={pilot.id}
                                    className="h-fit w-full text-left"
                                >
                                    <PilotCard
                                        user={pilot}
                                        currencyStatuses={statusesByUser.get(pilot.id)}
                                        inactive
                                    />
                                </UserProfileModalTrigger>
                            )
                        })}
                    </PilotCardGrid>
                </PilotCollapsibleSection>
            </div>
        </div>
    )
}

const PilotCollapsibleSection: FunctionComponent<{
    label: string
    pilots: MemberExtended[]
    defaultOpen?: boolean
    children: ReactNode
}> = ({ label, pilots, defaultOpen = false, children }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen)
    if (!pilots.length) return null
    return (
        <div className="flex flex-col">
            <button
                type="button"
                data-open={isOpen}
                className="flex items-center gap-3 py-2 w-full text-left text-gray-200 data-[open=true]:text-gray-0 hover:text-gray-0"
                onClick={() => setIsOpen((prev) => !prev)}
            >
                <Icons.Icon Icon={isOpen ? Icons.ArrowDropdown : Icons.ArrowDropdownRight} />
                <h5>{label}</h5>
                <h5 className="font-mono text-white-400">{pilots.length}</h5>
            </button>
            <ExpandableContent isOpen={isOpen}>{children}</ExpandableContent>
        </div>
    )
}

interface PilotViewProps {
    pilots: MemberExtended[]
    activeUserIds: Set<string>
}

const PilotCardGrid: FunctionComponent<{ children: ReactNode }> = ({ children }) => (
    <div className="w-full grid gap-3 grid-cols-[repeat(auto-fill,minmax(250px,1fr))] p-0.5">
        {children}
    </div>
)

const pilotStatsQuery = createQuery({
    resource: "metadata",
    select: ["mission.user_id"],
    aggregates: [
        { field: "airtime_hours", func: "SUM", alias: "totalFlightTime" },

        { field: "mission_id", func: "COUNT", alias: "totalFlights" },

        { field: "started_at", func: "MAX", alias: "lastFlightDate" },
    ],
    filters: [{ field: "mission.user_id", op: "IS NOT NULL", value: null }],
})
const pilotCertsQuery = createQuery({
    resource: "user_certificates",
    select: ["id", "user_id", "expires_at", "name"],
})

type PilotSortField =
    | "name"
    | "daysSinceLastFlight"
    | "totalFlights"
    | "totalFlightTime"
    | "currency"
    | "certStats"
type SortDir = "ASC" | "DESC"

const CURRENCY_SORT_RANK = { overdue: 0, upcoming: 1, current: 2 } as const

export const PilotTableView: FunctionComponent<PilotViewProps> = ({ pilots, activeUserIds }) => {
    const router = useRouter("/workspace/:workspaceId")
    const { data: statsData, isLoading: loadingStats } = usePilotTrackingQuery(pilotStatsQuery)
    const { data: certsData, isLoading: loadingCerts } = usePilotTrackingQuery(pilotCertsQuery)
    const { statusesByUser } = usePilotCurrencyStatuses()
    const loadingText = useLoadingText(loadingStats || loadingCerts)

    const [sortField, setSortField] = useState<PilotSortField>("name")
    const [sortDir, setSortDir] = useState<SortDir>("ASC")

    const getSortClickHandler = useCallback(
        (field: PilotSortField) => () => {
            if (sortField !== field) {
                setSortField(field)
                setSortDir("ASC")
            } else {
                setSortDir((d) => (d === "ASC" ? "DESC" : "ASC"))
            }
        },
        [sortField]
    )

    const tableRows = useMemo(() => {
        if (!statsData || !certsData) return []

        const now = new Date().getTime()

        const rows = pilots.map((user) => {
            const userStats = statsData.find((s) => s["mission.user_id"] === user.id)
            const userCerts = certsData.filter((c) => c.user_id === user.id)

            const totalCertsCount = userCerts.length
            const expiredCerts = userCerts.filter(
                (c) => c.expires_at != null && new Date(c.expires_at).getTime() < now
            )

            let daysSinceLastFlight = null
            if (userStats?.lastFlightDate) {
                const lastFlightTime = new Date(userStats.lastFlightDate).getTime()
                daysSinceLastFlight = Math.floor((now - lastFlightTime) / (1000 * 3600 * 24))
            }

            const currencyStatuses = user.id ? statusesByUser.get(user.id) : undefined
            return {
                ...user,
                isActive: activeUserIds.has(user.id),
                totalFlights: userStats?.totalFlights ?? 0,
                totalFlightTime: userStats?.totalFlightTime ?? 0,
                daysSinceLastFlight,
                currency: currencyStatuses?.length
                    ? summarizeCurrencyStatuses(currencyStatuses)
                    : null,
                certStats: { totalCount: totalCertsCount, expired: expiredCerts },
            }
        })

        const dir = sortDir === "ASC" ? 1 : -1
        return rows.toSorted((a, b) => {
            const primary = (() => {
                switch (sortField) {
                    case "name":
                        return dir * (a.fullName || "").localeCompare(b.fullName || "")
                    case "daysSinceLastFlight": {
                        if (a.daysSinceLastFlight === null && b.daysSinceLastFlight === null)
                            return 0
                        if (a.daysSinceLastFlight === null) return 1
                        if (b.daysSinceLastFlight === null) return -1
                        return dir * (a.daysSinceLastFlight - b.daysSinceLastFlight)
                    }
                    case "totalFlights":
                        return dir * (a.totalFlights - b.totalFlights)
                    case "totalFlightTime":
                        return dir * (a.totalFlightTime - b.totalFlightTime)
                    case "currency": {
                        const rank = (c: typeof a.currency) => (c ? CURRENCY_SORT_RANK[c.state] : 3)
                        return dir * (rank(a.currency) - rank(b.currency))
                    }
                    case "certStats": {
                        const expiredDiff = a.certStats.expired.length - b.certStats.expired.length
                        if (expiredDiff !== 0) return dir * expiredDiff
                        return dir * (a.certStats.totalCount - b.certStats.totalCount)
                    }
                    default:
                        return 0
                }
            })()
            if (primary !== 0 || sortField === "name") return primary
            return (a.fullName || "").localeCompare(b.fullName || "")
        })
    }, [pilots, activeUserIds, statsData, certsData, statusesByUser, sortField, sortDir])

    if (loadingStats || loadingCerts) return <div>{loadingText}</div>

    return (
        <Table className="h-full">
            <Table.Head>
                <Table.Header
                    className="cursor-pointer select-none"
                    onClick={getSortClickHandler("name")}
                >
                    <div className="flex items-center gap-1">
                        <div>Name</div>
                        <Table.SortIndicator sortState={sortField === "name" ? sortDir : "none"} />
                    </div>
                </Table.Header>
                <Table.Header
                    className="cursor-pointer select-none"
                    onClick={getSortClickHandler("daysSinceLastFlight")}
                >
                    <div className="flex items-center gap-1">
                        <div>Days since last flight</div>
                        <Table.SortIndicator
                            sortState={sortField === "daysSinceLastFlight" ? sortDir : "none"}
                        />
                    </div>
                </Table.Header>
                <Table.Header
                    className="cursor-pointer select-none"
                    onClick={getSortClickHandler("totalFlights")}
                >
                    <div className="flex items-center gap-1">
                        <div>Total flights</div>
                        <Table.SortIndicator
                            sortState={sortField === "totalFlights" ? sortDir : "none"}
                        />
                    </div>
                </Table.Header>
                <Table.Header
                    className="cursor-pointer select-none"
                    onClick={getSortClickHandler("totalFlightTime")}
                >
                    <div className="flex items-center gap-1">
                        <div>Total flight time</div>
                        <Table.SortIndicator
                            sortState={sortField === "totalFlightTime" ? sortDir : "none"}
                        />
                    </div>
                </Table.Header>
                <Table.Header
                    className="cursor-pointer select-none"
                    onClick={getSortClickHandler("currency")}
                >
                    <div className="flex items-center gap-1">
                        <div>Currency</div>
                        <Table.SortIndicator
                            sortState={sortField === "currency" ? sortDir : "none"}
                        />
                    </div>
                </Table.Header>
                <Table.Header
                    className="cursor-pointer select-none"
                    onClick={getSortClickHandler("certStats")}
                >
                    <div className="flex items-center gap-1">
                        <div>Certification</div>
                        <Table.SortIndicator
                            sortState={sortField === "certStats" ? sortDir : "none"}
                        />
                    </div>
                </Table.Header>
                <Table.Header className="sticky right-0 w-px max-w-px p-0" />
            </Table.Head>
            <Table.Body>
                {tableRows.map((row) => {
                    const dim = row.isActive ? "" : "opacity-50"
                    return (
                        <Table.Row key={row.id} className="group/pilotrow">
                            <Table.Cell className={dim}>
                                {row.isActive ? (
                                    <router.Link
                                        to={{
                                            path: "/fleet/pilots/:pilotId",
                                            params: { pilotId: row.userId },
                                        }}
                                    >
                                        <LinkText>{row.fullName}</LinkText>
                                    </router.Link>
                                ) : (
                                    <UserProfileModalTrigger userId={row.id}>
                                        <LinkText>{row.fullName}</LinkText>
                                    </UserProfileModalTrigger>
                                )}
                            </Table.Cell>

                            <Table.Cell className={dim}>
                                {row.daysSinceLastFlight !== null
                                    ? pluralize({ count: row.daysSinceLastFlight, str: "day" })
                                    : "No flights"}
                            </Table.Cell>

                            <Table.Cell className={dim}>
                                {pluralize({ count: row.totalFlights, str: "flight" })}
                            </Table.Cell>

                            <Table.Cell className={dim}>
                                {pluralize({ count: Math.round(row.totalFlightTime), str: "hour" })}
                            </Table.Cell>

                            <Table.Cell>
                                <CurrencyStatusCell summary={row.currency} />
                            </Table.Cell>

                            <Table.Cell>
                                <CertificationStatusCell
                                    expired={row.certStats.expired}
                                    totalCount={row.certStats.totalCount}
                                />
                            </Table.Cell>

                            <Table.Cell className="sticky right-0 w-px p-0">
                                <div className="absolute right-0 top-px bottom-px flex items-center gap-4 px-4 py-2 z-10 transition-opacity opacity-0 group-hover/pilotrow:opacity-100">
                                    {row.isActive ? (
                                        <ButtonPrimary
                                            title="View Profile"
                                            RightIcon={Icons.ArrowRight}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                router.navigate({
                                                    path: "/fleet/pilots/:pilotId",
                                                    params: { pilotId: row.userId },
                                                })
                                            }}
                                        />
                                    ) : (
                                        <UserProfileModalTrigger userId={row.id}>
                                            <ButtonPrimary
                                                title="View Profile"
                                                RightIcon={Icons.ArrowRight}
                                            />
                                        </UserProfileModalTrigger>
                                    )}
                                </div>
                            </Table.Cell>
                        </Table.Row>
                    )
                })}
            </Table.Body>
        </Table>
    )
}

function CurrencyStatusCell({
    summary,
}: {
    summary: ReturnType<typeof summarizeCurrencyStatuses> | null
}) {
    if (!summary) return <span className="text-gray-500">—</span>
    if (summary.state === "current") return <span>{summary.label}</span>
    return (
        <Status type={summary.state === "overdue" ? "error" : "warning"} variant="outline">
            {summary.label}
        </Status>
    )
}

function CertificationStatusCell({
    totalCount,
    expired,
}: {
    totalCount: number
    expired: {
        id: string
        name: string
        user_id: string
        expires_at: Date | null
    }[]
}) {
    if (expired.length <= 0) return <span>{totalCount} up to date</span>
    if (expired.length === 1)
        return (
            <Status type="error" variant="outline">
                Expired -{" "}
                {expired[0].expires_at?.toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                }) ?? ""}
            </Status>
        )
    return (
        <div className="relative group/tooltip w-fit">
            <Status type="error" variant="outline">
                {expired.length} / {totalCount} Expired
            </Status>
            <Tooltip>
                <div className="flex flex-col items-end">
                    {expired.map((e) => (
                        <div key={e.id}>
                            {manuallyTruncateLongString(e.name, 20)} expired on{" "}
                            {formatExpiredCertDateString(e.expires_at)}
                        </div>
                    ))}
                </div>
            </Tooltip>
        </div>
    )
}

function formatExpiredCertDateString(date: Date | null) {
    if (!date) return ""
    const month = date.getMonth() + 1
    const day = date.getDate()
    const year = date.getFullYear()
    return `${month.toString().padStart(2, "0")}/${day.toString().padStart(2, "0")}/${year}`
}
